// src/pages/shop/ShopDebtorCard.jsx
import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { debtors as debtorsApi, counterparties as cpApi } from '../../api';
import { Html5Qrcode } from 'html5-qrcode';
import Icon from '../../components/Icon';
import ScanButton from '../../components/ScanButton';
import { formatMoney } from '../../data/currencies';
import { useMyCurrency } from '../../hooks/useMyShop';
import { useToast } from '../../hooks/useToast';
import { errorMessage } from '../../utils/errorMessage';

const onlyDigits = (s) => (s || '').replace(/\D/g, '');

export default function ShopDebtorCard() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams();
  const currency = useMyCurrency();
  const { show, ToastView } = useToast();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [payOpen, setPayOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  // Привязка к аккаунту клиента: он видит свой долг и что брал
  const [linking, setLinking] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    debtorsApi.get(id)
      .then(setData)
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [id]);
  useEffect(() => { load(); }, [load]);

  if (loading) return <div className="loader"><div className="spinner" /></div>;
  if (!data) return (
    <div className="page"><div className="page-header">
      <button className="back-btn" onClick={() => navigate('/debtors')}>
        <Icon name="chevronLeft" size={18} />
      </button>
      <h1 className="page-title">{t('debtors.notFound')}</h1>
    </div></div>
  );

  // Магазин привязывает должника к аккаунту клиента по его личному QR.
  // Товар мог уехать курьером — тогда клиент присылает код картинкой в WhatsApp,
  // и мы загружаем её из галереи. Камера нужна не всегда.
  const linkByCode = async (code) => {
    setLinking(true);
    try {
      await cpApi.linkCustomer(id, code);
      show(t('debtorLink.linked'));
      load();
    } catch (e) {
      show(errorMessage(e, t), 'error');
    }
    setLinking(false);
  };

  const pickQrFromGallery = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.onchange = async (e) => {
      const file = e.target.files?.[0];
      if (!file) return;
      setLinking(true);
      try {
        const scanner = new Html5Qrcode('debtor-qr-reader');
        const decoded = await scanner.scanFile(file, false);
        await linkByCode(decoded);
      } catch (err) {
        show(t('debtorLink.noQrInImage'), 'error');
        setLinking(false);
      }
    };
    input.click();
  };

  const unlink = async () => {
    setLinking(true);
    try {
      await cpApi.unlinkCustomer(id);
      show(t('debtorLink.unlinked'));
      load();
    } catch (e) {
      show(errorMessage(e, t), 'error');
    }
    setLinking(false);
  };

  const fmtDate = (iso) => new Date(iso).toLocaleString(i18n.language || undefined, { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });

  const waLink = () => {
    const num = onlyDigits(data.whatsapp);
    if (!num) return null;
    const msg = t('debtors.waReminder', { amount: formatMoney(data.debt, currency) });
    return `https://wa.me/${num}?text=${encodeURIComponent(msg)}`;
  };
  const wa = waLink();

  return (
    <div className="page">
      <div className="page-header">
        <button className="back-btn" onClick={() => navigate('/debtors')}>
        <Icon name="chevronLeft" size={18} />
      </button>
        <h1 className="page-title" style={{ flex: 1 }}>{data.name}</h1>
        <button onClick={() => setEditOpen(true)} className="btn btn-soft" style={{ padding: '8px 10px' }}>
          <Icon name="settings" size={15} />
        </button>
      </div>

      <div style={{ padding: '0 20px' }}>
        {data.creditBlocked && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 12px', marginBottom: 12, background: 'var(--danger-soft, #fde8e8)', border: '1px solid var(--danger)', borderRadius: 'var(--r-sm)', color: 'var(--danger)', fontSize: 14, fontWeight: 700 }}>
            <Icon name="alert" size={16} /> {t('debtors.blacklisted')}
          </div>
        )}
        {/* Долг */}

        <div className="hero-shop" style={{ marginBottom: 12 }}>
          <p style={{ fontSize: 11, opacity: 0.85, fontWeight: 700, letterSpacing: '0.08em' }}>{t('debtors.currentDebt').toUpperCase()}</p>
          <p className="hero-num" style={{ marginTop: 6 }}>{formatMoney(data.debt, currency)}</p>
        </div>

        {/* Действия */}
        <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
          <button className="btn btn-primary" style={{ flex: 1 }} disabled={data.debt <= 0} onClick={() => setPayOpen(true)}>
            <Icon name="wallet" size={16} /> {t('debtors.acceptPayment')}
          </button>
          {wa && (
            <a className="btn btn-secondary" href={wa} target="_blank" rel="noreferrer" style={{ flex: 1, textDecoration: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
              <Icon name="whatsapp" size={16} /> {t('debtors.remindWhatsApp')}
            </a>
          )}
        </div>

        {data.notes && (
          <div className="card" style={{ padding: 12, marginTop: 8, fontSize: 13, color: 'var(--fg-muted)' }}>{data.notes}</div>
        )}

        {/* Привязка к аккаунту клиента: он увидит свой долг и что брал */}
        <div className="card" style={{ padding: 14, marginTop: 8 }}>
          <p style={{ fontSize: 11, fontWeight: 700, color: 'var(--fg-muted)', letterSpacing: '0.06em', marginBottom: 8 }}>
            {t('debtorLink.title')}
          </p>

          {data.linkedUser ? (
            <>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
                <div style={{
                  width: 34, height: 34, borderRadius: '50%', flexShrink: 0,
                  background: 'var(--success-soft, #DCFCE7)', color: 'var(--success)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <Icon name="check" size={16} stroke={2.5} />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <p style={{ fontSize: 13, fontWeight: 600 }}>
                    {data.linkedUser.name || data.linkedUser.email}
                  </p>
                  <p style={{ fontSize: 11, color: 'var(--fg-muted)' }}>{t('debtorLink.sees')}</p>
                </div>
              </div>
              <button
                className="btn btn-secondary"
                onClick={unlink}
                disabled={linking}
                style={{ width: '100%' }}
              >
                {t('debtorLink.unlink')}
              </button>
            </>
          ) : (
            <>
              <p style={{ fontSize: 12, color: 'var(--fg-muted)', marginBottom: 10, lineHeight: 1.5 }}>
                {t('debtorLink.hint')}
              </p>
              <div style={{ display: 'flex', gap: 8 }}>
                <ScanButton
                  variant="button"
                  raw
                  disabled={linking}
                  label={linking ? t('common.loading') : t('common.scan')}
                  style={{ flex: 1, justifyContent: 'center' }}
                  onScan={(code) => { if (code) linkByCode(code); }}
                />
                <button
                  className="btn btn-secondary"
                  onClick={pickQrFromGallery}
                  disabled={linking}
                  style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}
                >
                  <Icon name="image" size={16} /> {t('debtorLink.fromGallery')}
                </button>
              </div>
            </>
          )}
          {/* Скрытый контейнер — html5-qrcode требует элемент для распознавания файла */}
          <div id="debtor-qr-reader" style={{ display: 'none' }} />
        </div>

        {/* Оплаты */}
        {data.payments && data.payments.length > 0 && (
          <>
            <SectionLabel>{t('debtors.payments')}</SectionLabel>
            <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
              {data.payments.map((p, i) => (
                <div key={p.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 14px', borderTop: i === 0 ? 'none' : '1px solid var(--border-subtle)' }}>
                  <span style={{ fontSize: 12, color: 'var(--fg-faint)' }}>{fmtDate(p.createdAt)}</span>
                  <span style={{ fontSize: 14, fontWeight: 700, color: 'var(--success)', fontVariantNumeric: 'tabular-nums' }}>+{formatMoney(p.amount, currency)}</span>
                </div>
              ))}
            </div>
          </>
        )}

        {/* Продажи в долг */}
        {data.sales && data.sales.length > 0 && (
          <>
            <SectionLabel>{t('debtors.debtSales')}</SectionLabel>
            <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
              {data.sales.map((s, i) => (
                <div key={s.id} style={{ padding: '12px 14px', borderTop: i === 0 ? 'none' : '1px solid var(--border-subtle)' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ fontSize: 12, color: 'var(--fg-faint)' }}>{fmtDate(s.createdAt)}</span>
                    <span style={{ fontSize: 14, fontWeight: 700, color: 'var(--danger)', fontVariantNumeric: 'tabular-nums' }}>{formatMoney(s.creditAmount, currency)}</span>
                  </div>
                  <p style={{ fontSize: 12, color: 'var(--fg-muted)', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {(s.items || []).map(it => `${it.name}${it.variantName ? ` (${it.variantName})` : ''} ×${it.qty}`).join(', ')}
                  </p>
                </div>
              ))}
            </div>
          </>
        )}

        <div style={{ height: 24 }} />
      </div>

      {payOpen && (
        <PaymentSheet debt={data.debt} currency={currency}
          onClose={() => setPayOpen(false)}
          onSubmit={async (amount) => {
            try {
              await debtorsApi.payment(id, { amount });
              setPayOpen(false);
              load();
            } catch (e) { show(errorMessage(e, t), 'error'); }
          }} />
      )}

      {editOpen && (
        <EditSheet debtor={data}
          onClose={() => setEditOpen(false)}
          onSaved={() => { setEditOpen(false); load(); }}
          onError={(e) => show(errorMessage(e, t), 'error')} />
      )}
      {ToastView}
    </div>
  );
}

function SectionLabel({ children }) {
  return <p style={{ fontSize: 11, fontWeight: 700, color: 'var(--fg-muted)', letterSpacing: '0.08em', margin: '20px 0 10px 4px' }}>{children}</p>;
}

function PaymentSheet({ debt, currency, onClose, onSubmit }) {
  const { t } = useTranslation();
  const [amount, setAmount] = useState(String(debt));
  const [busy, setBusy] = useState(false);
  const num = Math.min(Math.max(0, Number(amount) || 0), debt);

  return (
    <div className="modal-backdrop" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="modal-sheet">
        <h3 className="modal-title">{t('debtors.acceptPayment')}</h3>
        <p style={{ fontSize: 13, color: 'var(--fg-muted)', marginBottom: 10 }}>
          {t('debtors.currentDebt')}: <b style={{ color: 'var(--danger)' }}>{formatMoney(debt, currency)}</b>
        </p>
        <input className="input" type="number" inputMode="decimal" value={amount} autoFocus
          onChange={(e) => setAmount(e.target.value)} style={{ marginBottom: 6 }} />
        <p style={{ fontSize: 12, color: 'var(--fg-faint)', marginBottom: 12 }}>
          {t('debtors.remainsAfter')}: {formatMoney(Math.max(0, debt - num), currency)}
        </p>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn btn-ghost" onClick={onClose} style={{ flex: 1 }}>{t('common.cancel')}</button>
          <button className="btn btn-primary" disabled={num <= 0 || busy} style={{ flex: 2 }}
            onClick={async () => { setBusy(true); await onSubmit(num); setBusy(false); }}>
            {busy ? '…' : t('debtors.acceptPayment')}
          </button>
        </div>
      </div>
    </div>
  );
}

function EditSheet({ debtor, onClose, onSaved, onError }) {
  const { t } = useTranslation();
  const [name, setName] = useState(debtor.name || '');
  const [whatsapp, setWhatsapp] = useState(debtor.whatsapp || '');
  const [notes, setNotes] = useState(debtor.notes || '');
  const [creditBlocked, setCreditBlocked] = useState(!!debtor.creditBlocked);
  const [busy, setBusy] = useState(false);

  const save = async () => {
    if (!name.trim() || busy) return;
    setBusy(true);
    try {
      await debtorsApi.update(debtor.id, { name: name.trim(), whatsapp: whatsapp.trim(), notes: notes.trim(), creditBlocked });
      onSaved();
    } catch (e) { onError(e); setBusy(false); }
  };

  return (
    <div className="modal-backdrop" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="modal-sheet">
        <h3 className="modal-title">{t('debtors.edit')}</h3>
        <label style={{ fontSize: 12, color: 'var(--fg-muted)' }}>{t('debtors.name')}</label>
        <input className="input" value={name} onChange={(e) => setName(e.target.value)} style={{ marginBottom: 8, marginTop: 4 }} />
        <label style={{ fontSize: 12, color: 'var(--fg-muted)' }}>{t('debtors.whatsapp')}</label>
        <input className="input" value={whatsapp} onChange={(e) => setWhatsapp(e.target.value)} style={{ marginBottom: 8, marginTop: 4 }} />
        <label style={{ fontSize: 12, color: 'var(--fg-muted)' }}>{t('debtors.notes')}</label>
        <textarea className="input" value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} style={{ marginBottom: 12, marginTop: 4, resize: 'vertical' }} />
        <button
          type="button"
          onClick={() => setCreditBlocked(v => !v)}
          style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%', padding: '12px 14px', marginBottom: 12, background: creditBlocked ? 'var(--danger-soft, #fde8e8)' : 'var(--bg-subtle)', border: `1px solid ${creditBlocked ? 'var(--danger)' : 'var(--border)'}`, borderRadius: 'var(--r-sm)', cursor: 'pointer', textAlign: 'left' }}
        >
          <span style={{ width: 22, height: 22, borderRadius: 6, border: `2px solid ${creditBlocked ? 'var(--danger)' : 'var(--border-strong, #ccc)'}`, background: creditBlocked ? 'var(--danger)' : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            {creditBlocked && <Icon name="check" size={14} stroke={3} color="#fff" />}
          </span>
          <span style={{ flex: 1 }}>
            <span style={{ display: 'block', fontSize: 14, fontWeight: 600, color: creditBlocked ? 'var(--danger)' : 'var(--fg)' }}>{t('debtors.blockCredit')}</span>
            <span style={{ display: 'block', fontSize: 12, color: 'var(--fg-muted)' }}>{t('debtors.blockCreditHint')}</span>

          </span>
        </button>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn btn-ghost" onClick={onClose} style={{ flex: 1 }}>{t('common.cancel')}</button>
          <button className="btn btn-primary" disabled={!name.trim() || busy} style={{ flex: 2 }} onClick={save}>{busy ? '…' : t('common.save')}</button>
        </div>
      </div>
    </div>
  );
}
